import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiEndpoint } from "../../urls";
import { refreshToken } from "./pdfEditorTokenSlice";

// Load initial state from localStorage
const savedDocument = localStorage.getItem("uploadedDocument");

export const uploadDocument = createAsyncThunk(
  "uploadDocument/upload",
  async ({ token, file, files, fileName, bulkEsign, stampId }, { dispatch, rejectWithValue }) => {
    const performUpload = async (authToken) => {
      const formData = new FormData();

      const getFileNameFromFileItem = (item, fallbackName) => {
        if (!item) return fallbackName || "document.pdf";
        if (item.file && item.file.name) return item.file.name;
        if (item.name) return item.name;
        return fallbackName || "document.pdf";
      };

      const isStampPaperItem = (item) => {
        if (!item) return false;
        if (item.type && item.type.toLowerCase() === "stamp") return true;
        const name = getFileNameFromFileItem(item, "");
        return name.toLowerCase().includes("stamp");
      };

      if (files && files.length > 0) {
        // ✅ Multiple files case
        files.forEach((f, index) => {
          const fileObject = f?.file;
          const nameToSend = getFileNameFromFileItem(f, fileName);
          // If first item is stamp paper, send the next file name for the first doc name
          const adjustedName =
            index === 0 && isStampPaperItem(f) && files.length > 1
              ? getFileNameFromFileItem(files[1], fileName)
              : nameToSend;

          formData.append("document", fileObject, adjustedName);
          formData.append("filename", adjustedName);
        });
      } else if (file) {
        // ✅ Single file case (backward-compatible)
        formData.append("document", file?.file, fileName);
      }
      formData.append("source", "website");
      formData.append("bulk_esign", bulkEsign);

      const response = await axios.post(
        `${apiEndpoint.pdfBaseUri}/editor/upload_document/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const documentData = { ...response.data, fullView: true, stamp_id: stampId };
      console.log(documentData);
      localStorage.setItem("uploadedDocument", JSON.stringify(documentData));
      return documentData;
    };

    try {
      return await performUpload(token);
    } catch (error) {
      const status = error?.response?.status;

      if (status === 401) {
        console.log("401 detected, attempting to refresh token...");
        const refreshResult = await dispatch(refreshToken());
        console.log(refreshResult);
        if (refreshToken.fulfilled.match(refreshResult)) {
          const newToken = refreshResult.payload?.access;
          if (!newToken) return rejectWithValue("Token refresh failed.");

          try {
            return await performUpload(newToken); // retry with new token
          } catch (retryError) {
            const msg = retryError?.response?.data?.message || "Retry upload failed.";
            return rejectWithValue(msg);
          }
        } else {
          return rejectWithValue("Token refresh failed.");
        }
      }

      const msg = error?.response?.data?.message || "Upload failed.";
      return rejectWithValue(msg);
    }
  }
);

let parsedSavedDoc = null;
try {
  parsedSavedDoc = savedDocument ? JSON.parse(savedDocument) : null;
} catch (e) {
  parsedSavedDoc = null;
}
const initialDocData = parsedSavedDoc?.data || parsedSavedDoc;
const initialAttachedStampPageNo = (initialDocData?.atched_stamp_page_no !== undefined && initialDocData?.atched_stamp_page_no !== null)
  ? Number(initialDocData.atched_stamp_page_no)
  : (initialDocData?.other_actions?.single_page && Object.keys(initialDocData.other_actions.single_page).length > 0
    ? Number(Object.keys(initialDocData.other_actions.single_page)[0])
    : null);

const initialIsStampRequired = Boolean(
  initialDocData?.is_stamrequred ||
  initialDocData?.is_stamrequired ||
  initialDocData?.is_stamp_required ||
  initialDocData?.isStampRequired ||
  initialAttachedStampPageNo ||
  initialDocData?.document_template?.stamp_coordinate
);

const initialState = {
  uploadedDocument: parsedSavedDoc,
  isLoading: false,
  error: null,
  isStampRequired: initialIsStampRequired,
  attachedStampPageNo: initialAttachedStampPageNo,
  stampCoordinates: [],
};

const uploadDocumentSlice = createSlice({
  name: "uploadDocument",
  initialState,
  reducers: {
    setUploadedDocument: (state, action) => {
      state.uploadedDocument = action.payload;
      const docData = action.payload?.data || action.payload;

      let stampPg = null;
      if (docData?.atched_stamp_page_no !== undefined && docData?.atched_stamp_page_no !== null) {
        stampPg = Number(docData.atched_stamp_page_no);
      } else if (docData?.other_actions?.single_page && Object.keys(docData.other_actions.single_page).length > 0) {
        stampPg = Number(Object.keys(docData.other_actions.single_page)[0]);
      }

      state.attachedStampPageNo = stampPg;

      const hasStamp = Boolean(
        docData?.is_stamrequred ||
        docData?.is_stamrequired ||
        docData?.is_stamp_required ||
        docData?.isStampRequired ||
        stampPg ||
        docData?.document_template?.stamp_coordinate
      );

      state.isStampRequired = hasStamp;
      localStorage.setItem("uploadedDocument", JSON.stringify(action.payload)); // Keep storage updated
    },
    clearUploadedDocument: (state) => {
      state.uploadedDocument = null;
      state.isStampRequired = false;
      state.attachedStampPageNo = null;
      state.stampCoordinates = [];
      localStorage.removeItem("uploadedDocument");
      window.location.replace(`${process.env.PUBLIC_URL}/esign/documents?activeTab=1`);
    },
    clearUploadedDocumentFromTab: (state) => {
      state.uploadedDocument = null;
      state.isStampRequired = false;
      state.attachedStampPageNo = null;
      state.stampCoordinates = [];
      localStorage.removeItem("uploadedDocument");
    },
    updateDocumentName: (state, action) => {
      if (state.uploadedDocument?.data?.document_id === action.payload.document_id) {
        state.uploadedDocument.data.document_name = action.payload.document_name;
        localStorage.setItem("uploadedDocument", JSON.stringify(state.uploadedDocument));
      }
    },
    toggleFullView: (state) => {
      if (state.uploadedDocument) {
        state.uploadedDocument.fullView = true;
        localStorage.setItem("uploadedDocument", JSON.stringify(state.uploadedDocument));
      }
    },
    updateDocumentTemplate: (state, action) => {
      if (state.uploadedDocument?.data) {
        state.uploadedDocument.data.document_template = action.payload;
        localStorage.setItem("uploadedDocument", JSON.stringify(state.uploadedDocument));
      }
    },
    setIsStampRequired: (state, action) => {
      state.isStampRequired = Boolean(action.payload);
      if (!action.payload) {
        state.attachedStampPageNo = null;
        state.stampCoordinates = [];
      }
    },
    setAttachedStampPageNo: (state, action) => {
      state.attachedStampPageNo = action.payload;
      if (action.payload) {
        state.isStampRequired = true;
      }
    },
    setStampCoordinates: (state, action) => {
      state.stampCoordinates = action.payload || [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadDocument.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.uploadedDocument = action.payload;
        state.isStampRequired = false;
        state.attachedStampPageNo = null;
        state.stampCoordinates = [];
        localStorage.setItem("uploadedDocument", JSON.stringify(action.payload));
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setUploadedDocument,
  clearUploadedDocument,
  clearUploadedDocumentFromTab,
  updateDocumentName,
  toggleFullView,
  updateDocumentTemplate,
  setIsStampRequired,
  setAttachedStampPageNo,
  setStampCoordinates,
} = uploadDocumentSlice.actions;

export default uploadDocumentSlice.reducer;
